import React, { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { Form, FormListType } from './FormList';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
  getGridDateOperators,
  getGridStringOperators,
} from '@mui/x-data-grid';
import { debounce } from 'lodash';

interface SortableTableProps {
  rows: Form[];
  isLoading: boolean;
  formListType: FormListType;
  selectedFormIds: GridRowSelectionModel;
  setSelectedFormIds: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>;
  bulkActionInProgress: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  isFetchingNextPage: boolean;
  setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
}

export default function SortableTable({
  rows,
  isLoading,
  selectedFormIds,
  setSelectedFormIds,
  bulkActionInProgress,
  paginationModel,
  setPaginationModel,
  formListType,
  isFetchingNextPage,
  setFilterModel,
}: SortableTableProps) {
  const navigate = useNavigate();

  //slice returned forms list to display set of forms for given page
  const pageRows = rows.slice(
    paginationModel.page * paginationModel.pageSize,
    (paginationModel.page + 1) * paginationModel.pageSize
  );

  const debouncedFilterChange = useMemo(
    () =>
      debounce((model: GridFilterModel) => {
        setFilterModel(model);
      }, 400),
    [setFilterModel]
  );

  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  const containsOnly = useMemo(
    () => [getGridStringOperators().find((op) => op.value === 'contains')!],
    []
  );
  const columns = useMemo<GridColDef<Form>[]>(
    () => [
      {
        field: 'formNumber',
        headerName: 'Form Number',
        flex: 2,
        minWidth: 120,
        filterable: true,
        filterOperators: containsOnly,
      },
      {
        field: 'formTitle',
        headerName: 'Form Title',
        flex: 1,
        minWidth: 140,
        filterable: true,
        filterOperators: containsOnly,
      },
      {
        field: 'userEmail',
        headerName: 'User Email',
        flex: 1,
        minWidth: 160,
        filterable: true,
        filterOperators: containsOnly,
      },
      {
        field: 'dateModified',
        headerName: 'Date Modified',
        flex: 1,
        minWidth: 160,
        filterable: true,
        type: 'dateTime',
        filterOperators: getGridDateOperators().filter((op) => ['onOrAfter'].includes(op.value)),
        valueGetter: (params) => (params.value ? new Date(params.value as string) : null),
      },
    ],
    [containsOnly]
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: 0,
        marginTop: 1,
      }}
    >
      <DataGrid
        //composite key to support publog
        getRowId={(row) => `${row.id}#${row.dateModified}`}
        loading={
          isLoading ||
          (isFetchingNextPage && pageRows.length < paginationModel.pageSize) ||
          bulkActionInProgress
        }
        rows={pageRows}
        columns={columns}
        disableColumnMenu={false}
        checkboxSelection={formListType === 'UNPUBLISHED'}
        disableRowSelectionOnClick
        onRowSelectionModelChange={(ids) => setSelectedFormIds(ids)}
        rowSelectionModel={selectedFormIds}
        onRowClick={(params) => {
          const id = params.id.toString().split('#')[0];
          if (bulkActionInProgress) {
            return;
          }
          navigate(`/forms/${id}`);
        }}
        isRowSelectable={(params) =>
          bulkActionInProgress ? !selectedFormIds.includes(params.id as string) : true
        }
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={(newPaginationModel: GridPaginationModel) =>
          setPaginationModel(newPaginationModel)
        }
        filterMode="server"
        onFilterModelChange={(model) => {
          debouncedFilterChange(model);
        }}
        rowCount={rows.length ?? 0}
        sx={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiTablePagination-displayedRows': {
            marginBottom: 0,
          },
          ...(bulkActionInProgress && {
            '& .MuiTablePagination-actions .MuiButtonBase-root': {
              pointerEvents: 'none',
              opacity: 0.4,
              cursor: 'default',
            },
          }),
        }}
      />
    </Box>
  );
}
